import { db } from '@/config/firebase';
import { 
  collection, 
  query, 
  where, 
  getDocs,
  DocumentData,
  Timestamp
} from 'firebase/firestore';
import { AVAILABLE_APPS } from '@/services/appAccess';
import { firestoreWrapper } from './firestoreWrapper';

export interface UsageMetrics {
  activeUsers: {
    total: number;
    byApp: Record<string, number>;
  };
  goodThings: {
    total: number;
    fromApp: number;
    fromSheets: number;
  };
}

// Helper function to parse any date format and convert to EST timezone
const parseAnyDateFormat = (dateStr: string): Date => {
  if (!dateStr) return new Date(0);
  
  // Try direct parsing first
  let date = new Date(dateStr);
  
  // If invalid or year is wrong, try MM/DD/YY format
  if (isNaN(date.getTime()) || date.getFullYear() < 2000) {
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts;
      // Assume 20xx for two-digit years
      date = new Date(`20${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
    }
  }
  
  return date;
};

// Helper function to get date in EST timezone
const getDateInEST = (date: Date): Date => {
  // Format the date in EST timezone
  const estOptions = { timeZone: 'America/New_York' };
  const estDateString = date.toLocaleDateString('en-US', estOptions);
  
  // Parse the EST date string back to a Date object
  const [month, day, year] = estDateString.split('/');
  return new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00-05:00`);
};

class MetricsService {
  /**
   * Get metrics for a specific date range
   * @param startDate Start date in YYYY-MM-DD format
   * @param endDate End date in YYYY-MM-DD format
   */
  async getMetrics(startDate: string, endDate: string): Promise<UsageMetrics> {
    try {
      // Create the query for active users and fromApp metrics
      const metricsQuery = query(
        collection(db, 'usageMetrics'),
        where('date', '>=', startDate),
        where('date', '<=', endDate)
      );
      
      // Execute the query
      const metricsSnap = await getDocs(metricsQuery);
      
      // Initialize the metrics object
      const metrics: UsageMetrics = {
        activeUsers: {
          total: 0,
          byApp: {}
        },
        goodThings: {
          total: 0,
          fromApp: 0,
          fromSheets: 0
        }
      };
      
      // Initialize byApp with all available apps
      Object.values(AVAILABLE_APPS).forEach(appId => {
        metrics.activeUsers.byApp[appId] = 0;
      });
      // Add hub app
      metrics.activeUsers.byApp['hub'] = 0;
      
      // Aggregate the active users and fromApp metrics
      metricsSnap.forEach(doc => {
        const data = doc.data();
        
        // Aggregate active users
        if (data.activeUsers) {
          metrics.activeUsers.total += data.activeUsers.total || 0;
          
          // Aggregate by app
          if (data.activeUsers.byApp) {
            Object.entries(data.activeUsers.byApp).forEach(([appId, count]) => {
              if (!metrics.activeUsers.byApp[appId]) {
                metrics.activeUsers.byApp[appId] = 0;
              }
              metrics.activeUsers.byApp[appId] += count as number;
            });
          }
        }
        
        // Aggregate Good Things fromApp count only
        if (data.goodThings) {
          metrics.goodThings.fromApp += data.goodThings.fromApp || 0;
        }
      });
      
      // Now query the actual Good Things collection to get the total count
      // Convert YYYY-MM-DD dates to Date objects in EST timezone
      const startDateObj = getDateInEST(new Date(startDate));
      const endDateObj = getDateInEST(new Date(endDate));
      
      // Adjust dates by one day to fix the off-by-one issue (shift forward)
      startDateObj.setDate(startDateObj.getDate() + 1);
      endDateObj.setDate(endDateObj.getDate() + 1);
      
      // Set end date to end of day
      endDateObj.setHours(23, 59, 59, 999);
      
      // Query all Good Things
      const goodThingsQuery = query(
        collection(db, 'goodThings')
      );
      
      const goodThingsSnap = await getDocs(goodThingsQuery);
      
      // Filter Good Things by date range
      let totalGoodThings = 0;
      
      goodThingsSnap.forEach(doc => {
        const data = doc.data();
        if (data.date) {
          // Parse the date and convert to EST timezone for comparison
          const entryDate = parseAnyDateFormat(data.date);
          const entryDateEST = getDateInEST(entryDate);
          
          // Check if the entry date is within the range
          if (entryDateEST >= startDateObj && entryDateEST <= endDateObj) {
            totalGoodThings++;
          }
        }
      });
      
      // Set the total count from the actual Good Things collection
      metrics.goodThings.total = totalGoodThings;
      
      // Calculate fromSheets as total - fromApp
      metrics.goodThings.fromSheets = Math.max(0, metrics.goodThings.total - metrics.goodThings.fromApp);
      
      return metrics;
    } catch (error) {
      throw error;
    }
  }
}

export const metricsService = new MetricsService();
